import { isApiError } from '../api/client';
import { t, tk, type DictKey, type Vars } from '../i18n';
import { uk } from '../i18n/uk';
import { formatMoney } from './money';

/** Коди, для яких деталь сервера ніколи не показуємо: там англійська, SQL або службові назви. */
const NO_DETAIL = new Set(['check_violation', 'duplicate', 'validation', 'rule_violation', 'db_error', 'invalid_reference', 'invalid_input']);
/** Коди, де деталь — гроші в копійках без суфікса _minor (старий формат). */
const MONEY_CODES = new Set(['min_order_not_met', 'credit_limit_exceeded']);

const has = (key: string): key is DictKey => key in uk;
/**
 * Коди API з власним текстом. Більшість кодів знаходять текст самі (`error.<code>`), але ці можуть прийти
 * і як деталь іншої помилки — тому перелічені явно, щоб не впасти в загальне «Файл завеликий» / «Помилка».
 */
const DETAIL_CODES: Record<string, DictKey> = {
  empty_file: 'error.empty_file',
  'file is empty': 'error.empty_file',
  name_required: 'error.name_required',
  already_booked: 'error.already_booked',
  cannot_edit_self: 'error.cannot_edit_self',
};
/** Деталь схожа на текст бази даних — не для людей. */
const looksTechnical = (s: string) => /violates|constraint|relation|column|syntax|null value|duplicate key|uuid|[0-9a-f]{8}-[0-9a-f]{4}-/i.test(s);
const hasCyrillic = (s: string) => /[а-яіїєґ]/i.test(s);

/** Текст помилки для людини: одне зрозуміле речення українською. */
export function errorText(e: unknown): string {
  if (isApiError(e)) {
    if (e.userText) return e.userText;
    const detail = e.detail ?? null;
    const dict = parseDict(detail);
    if (e.code === 'rule_violation') return detail && !dict ? ruleText(detail) : t('error.rule_violation');
    if (e.code === 'duplicate' && dict?.name) return t('error.dupName');
    if (detail && !dict && (e.code === 'duplicate' || e.code === 'check_violation' || e.code === 'invalid_reference')) {
      const c = constraintText(e.code, detail);
      if (c) return c;
    }
    // окремі коди, які сервер іноді кладе в деталь (403 forbidden: cannot_edit_self, 409 conflict: already_booked)
    const sub = detail && !dict ? DETAIL_CODES[detail.trim()] : undefined;
    if (sub) return t(sub);
    const key = `error.${e.code}`;
    if (has(key)) return fill(key, varsFor(e.code, detail, dict));
    // невідомий код: показуємо деталь лише якщо вона людська
    if (detail && !dict && !NO_DETAIL.has(e.code) && !looksTechnical(detail) && hasCyrillic(detail)) return detail;
    return e.status >= 500 ? t('error.server_error') : t('error.unknown');
  }
  if (e instanceof Error) return hasCyrillic(e.message) ? e.message : t('error.unknown');
  return t('error.unknown');
}

/** Деталь-словник з сервера ({free, need} тощо) — client.ts передає її як JSON-рядок. */
function parseDict(detail: string | null): Record<string, unknown> | null {
  if (!detail || detail[0] !== '{') return null;
  try {
    const v: unknown = JSON.parse(detail);
    return v && typeof v === 'object' && !Array.isArray(v) ? (v as Record<string, unknown>) : null;
  } catch {
    return null;
  }
}

function varsFor(code: string, detail: string | null, dict: Record<string, unknown> | null): Vars {
  const vars: Vars = {};
  for (const [k, v] of Object.entries(dict ?? {})) {
    // гроші приходять у копійках: {available_minor} → {available} = «1 234 ₴»
    if (typeof v === 'number' && k.endsWith('_minor')) vars[k.slice(0, -6)] = formatMoney(v, 'UAH', { compact: true });
    else if (typeof v === 'number') vars[k] = MONEY_CODES.has(code) ? formatMoney(v, 'UAH', { compact: true }) : v;
    else if (typeof v === 'string') vars[k] = v;
  }
  if (typeof dict?.max_bytes === 'number') vars.max = dict.max_bytes;
  if (code === 'wrong_status' && typeof vars.status === 'string') vars.status = tk('orderStatus', vars.status);
  if (code === 'invalid_size' || code === 'file_too_large') {
    const bytes = Number(vars.max ?? /(\d+)/.exec(detail ?? '')?.[1] ?? 0);
    if (bytes) vars.max = bytes > 1024 * 1024 ? Math.round(bytes / 1024 / 1024) : bytes;
  }
  // людська деталь (напр. перелік прострочених документів машини) — у {detail}
  if (detail && !dict && hasCyrillic(detail) && !looksTechnical(detail)) vars.detail = detail;
  return vars;
}

/** Підставляє змінні; якщо якоїсь бракує — лишає речення до неї, щоб не показати «{free}». */
function fill(key: DictKey, vars: Vars): string {
  const text = t(key, vars);
  const at = text.search(/\{\w+[|}]/);
  if (at < 0) return text;
  let head = text.slice(0, at);
  const cut = Math.max(head.lastIndexOf(':'), head.lastIndexOf('('));
  if (cut > 0) head = head.slice(0, cut);
  head = head.replace(/[\s,—«-]+$/u, '').trim();
  return head.length > 8 ? `${head}.` : t('error.unknown');
}

/** Назва обмеження БД (сервер віддає лише її) → речення. */
function constraintText(code: string, detail: string): string | null {
  const c = `error.c.${detail}`;
  if (has(c)) return t(c);
  if (code === 'duplicate') {
    if (/^order\b/.test(detail)) return t('error.c.orders_external');
    if (/^sku\b/.test(detail)) return t('error.sku_taken');
    if (/overlap/.test(detail)) return t('error.overlap');
    if (/_name_key$/.test(detail)) return t('error.dupName');
  }
  if (code === 'check_violation') {
    if (/stock location/.test(detail)) return t('error.noStockLocation');
    if (/^inventory_levels|stock|reserv/.test(detail)) return t('error.c.inventory_levels_check');
  }
  return null;
}

/** Правила, які сервер перевіряє з англ. технічною причиною → зрозумілий текст. */
const RULES: [RegExp, (m: RegExpExecArray) => string][] = [
  [/checklist(?: has)?:? (\d+) open required/, (m) => t('error.guardChecklist', { n: m[1]! })],
  [/^guard:checklist(?::(\d+))?/, (m) => (m[1] ? t('error.guardChecklist', { n: m[1] }) : t('error.guardFailed'))],
  [/^guard:(?:docs|documents)(?::(\d+))?/, (m) => (m[1] ? t('error.guardDocs', { n: m[1] }) : t('error.guardFailed'))],
  [/^guard:trip_booked/, () => t('error.guard_trip_booked')],
  [/^guard:order_confirmed/, () => t('error.guard_order_confirmed')],
  [/^guard:tasks_done(?::(\d+))?/, (m) => t('error.guard_tasks_done', { n: m[1] ?? '' })],
  [/^guard(?:_failed)?\b/, () => t('error.guardFailed')],
  [/^transition_not_allowed/, () => t('error.transitionNotAllowed')],
  [/^trip_transition/, () => t('error.trip_transition')],
  [/^shipment_transition/, () => t('error.shipmentTransition')],
  [/^already[_ ]received/, () => t('error.already_received')],
  [/^no_document/, () => t('error.no_document')],
  [/^order is already confirmed/, () => t('error.orderConfirmed')],
  [/^order is (\w+), expected/, (m) => t('error.orderStatus', { status: tk('orderStatus', m[1]) })],
  [/^order has no items/, () => t('error.orderNoItems')],
  [/^min_order_not_met: (-?\d+) < (-?\d+)/, (m) => t('error.min_order_not_met', { total: money(m[1]!), min: money(m[2]!) })],
  [/^credit_limit_exceeded: available (-?\d+)/, (m) => t('error.credit_limit_exceeded', { available: money(m[1]!) })],
  [/^b2b account is/, () => t('error.b2bStatus')],
  [/^absence is/, () => t('error.absenceDecided')],
  [/^no default price list/, () => t('error.noDefaultPrice')],
  [/anonymized/, () => t('error.contactAnonymized')],
  [/^vehicle type has booked places/, () => t('error.vehicleTypeBooked')],
  [/^vehicle is not active/, () => t('error.vehicleInactive')],
  [/^stop has loads/, () => t('error.stopHasLoads')],
  [/at least two stops/, () => t('error.tripTwoStops')],
  [/^trip is closed/, () => t('error.tripClosed')],
  [/^shipment is closed/, () => t('error.shipmentClosed')],
  [/^batch is fully loaded/, () => t('error.batchLoaded')],
  [/^po_loaded/, () => t('error.poLoaded')],
  [/^deceased required/, () => t('error.deceasedRequired')],
  [/at least one active owner/, () => t('error.keepOwner')],
  [/^attribute is used by products/, () => t('error.attrInUse')],
  [/^lead already converted/, () => t('error.leadConverted')],
  [/no pipeline order/, () => t('error.noPipelineOrder')],
  [/^apply a template first/, () => t('error.applyTemplate')],
  [/^generated by a block/, () => t('error.generatedByBlock')],
  [/^client may only mark/, () => t('error.clientUploadOnly')],
  [/^forbidden$/, () => t('error.forbidden')],
  [/^(?:empty_file|file is empty)/, () => t('error.empty_file')],
  [/^name_required/, () => t('error.name_required')],
  [/^already_booked/, () => t('error.already_booked')],
  [/^cannot_edit_self/, () => t('error.cannot_edit_self')],
];

const money = (minor: string) => formatMoney(Number(minor), 'UAH', { compact: true });

function ruleText(reason: string): string {
  for (const [re, fn] of RULES) {
    const m = re.exec(reason);
    if (m) return fn(m);
  }
  // гарди, написані українською, показуємо як є; англійські — загальним реченням
  return hasCyrillic(reason) && !looksTechnical(reason) ? reason : t('error.rule_violation');
}

/** Причини блокування переходу з сервера (англ. технічні) → зрозумілий текст. Невідомі — без змін. */
export function guardText(reason: string): string {
  for (const [re, fn] of RULES) {
    const m = re.exec(reason);
    if (m) return fn(m);
  }
  return reason;
}
